import { Chip, Grid, makeStyles, MenuItem, Paper, TextField, Typography } from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { planEatApi } from "../../../../planEatApi/PlanEatApi";
import { IUserContent } from "../../../../planEatApi/PlanEatFacade";
import { EntityDetailLayout, Section } from "../../components/EntityDetailLayout";
import { eaterGroupOptions, officeOptions } from "../../components/selectOptions";
import { EntityEditorConfig, useEntityEditor } from "../../components/useEntityEditor";
import { hierarchyCrumbs } from "../../components/VersionsPage";
import { useSysadminData } from "../../SysadminData";
import { safeDecode, sysadminPaths } from "../../sysadminUtils";
import { UserAccount } from "./UserAccount";

const useStyles = makeStyles(theme => ({
    chips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    loading: {
        padding: theme.spacing(3),
    },
}));

const userConfig: EntityEditorConfig<IUserContent> = {
    label: "User",
    section: "users",
    empty: {
        id: "",
        name: "",
        description: "",
        applicationUserId: "",
        officeId: "",
        eaterGroupId: "",
    },
    get: planEatApi.getUser,
    getVersion: planEatApi.getVersionForUser,
    post: planEatApi.postUser,
    remove: planEatApi.postDeleteForUser,
};

/** A historic version of the user's placement (read-only, can be restored). */
const UserVersion: React.FC<{ userId: string; version: string }> = ({ userId, version }) => {
    const classes = useStyles();
    const data = useSysadminData();
    const editor = useEntityEditor(userConfig, userId, version);
    const { entity: user } = editor;

    const groupsByUser = useMemo(
        () => new Map(data.serviceUsers.map(u => [u.content.id, u.content.data_raw?.groups ?? []])),
        [data.serviceUsers]
    );
    const eaterGroup = data.eaterGroupById(user.eaterGroupId);
    const title = user.applicationUserId || "Unnamed user";
    const accountGroups = groupsByUser.get(user.applicationUserId);

    return (
        <EntityDetailLayout
            label="User"
            section="users"
            title={title}
            breadcrumbs={[...hierarchyCrumbs(data, "users", { eaterGroupId: user.eaterGroupId, officeId: user.officeId }), { label: title }]}
            editor={editor}
            missing={[]}
            parentPath={user.applicationUserId ? sysadminPaths.detail("users", user.applicationUserId) : sysadminPaths.list("users")}
        >
            <Section title="Account" description="The application account (email) the user logs in with.">
                <TextField fullWidth variant="outlined" label="Account" value={user.applicationUserId} disabled />
                <div className={classes.chips}>
                    {accountGroups?.map(group => <Chip key={group} size="small" label={group} />)}
                </div>
            </Section>
            <Section title="Placement">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth select variant="outlined" label="Office" value={user.officeId ?? ""} disabled>
                            {officeOptions(data)}
                            {user.officeId && !data.officeById(user.officeId) && <MenuItem value={user.officeId} disabled>Unknown office</MenuItem>}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth select variant="outlined" label="Eater group" value={user.eaterGroupId ?? ""} disabled>
                            {eaterGroupOptions(data, user.officeId)}
                            {user.eaterGroupId && eaterGroup?.officeId !== user.officeId && <MenuItem value={user.eaterGroupId} disabled>{eaterGroup?.eaterGroupName ?? "Unknown eater group"}</MenuItem>}
                        </TextField>
                    </Grid>
                </Grid>
            </Section>
            {!data.loading && <Typography variant="caption" color="textSecondary">Only the placement is versioned. User groups are not part of the history.</Typography>}
        </EntityDetailLayout>
    );
};

/** Finds the PlanEat user id from the URL, which is either the email or (old links / history) the PlanEat user id. */
export function resolvePlanEatUserId(data: { users: { content: { id: string; applicationUserId: string } }[] }, param: string): string | undefined {
    const value = safeDecode(param);
    return (data.users.find(u => u.content.id === value) ?? data.users.find(u => u.content.applicationUserId === value))?.content.id;
}

export const User: React.FC = () => {
    const classes = useStyles();
    const { userId, version } = useParams<{ userId: string; version?: string }>();
    const data = useSysadminData();

    if (!version) return <UserAccount />;

    const planEatUserId = resolvePlanEatUserId(data, userId);
    if (!planEatUserId) {
        return data.loading
            ? <Paper variant="outlined" className={classes.loading}><Skeleton height={40} /><Skeleton height={40} /></Paper>
            : <Alert severity="error">No PlanEat user found for {safeDecode(userId)}</Alert>;
    }
    return <UserVersion userId={planEatUserId} version={version} />;
};
